import { Gender } from "../../../shared/common/api";
import { ByUuid, TickerApiMatchDay, TickerTimeInterval } from "../../../shared/common/ticker";
import {
  BeachTickerMatch,
  BeachTickerMatchDay,
  BeachTickerMatchDays,
  BeachTickerMatchSeries,
  BeachTickerMatchSeriesByUuid,
  BeachTickerHierarchies,
  BeachTickerAssociations
} from './model';
import {
  BeachTickerApiTeam,
  BeachTickerSettings
} from "../../../shared/beach/api";
import { formatDate, formatTime, getDayName } from "../utils";
import { BeachTickerMatchState } from "../../../shared/beach/ticker";
import { BeachTickerCachedMatch, BeachTickerCachedMatchSeries } from "../../../shared/beach/cache";

export interface BeachTickerMatchStatesByUuid {
  [matchUuid: string]: BeachTickerMatchState
}

export interface BeachTickerState {
  id?: string
  matchSeries: BeachTickerMatchSeriesByUuid
  matchDays: BeachTickerMatchDays
  matchStatesByUuid: BeachTickerMatchStatesByUuid
  settings: BeachTickerSettings
  loading: boolean
  filterNav?: Gender
  error?: string
  associations: BeachTickerAssociations
  hierarchies: BeachTickerHierarchies
}

const initialBeachTickerState: BeachTickerState = {
  matchSeries: {} as BeachTickerMatchSeriesByUuid,
  matchDays: [] as BeachTickerMatchDays,
  matchStatesByUuid: {},
  filterNav: undefined,
  associations: [],
  hierarchies: [],
  settings: {
    navLinkItems: [],
    timeInterval: {} as TickerTimeInterval
  } as BeachTickerSettings,
  loading: true
}

const LOAD_BEACH_TICKER_STATE_SUCCESS = "[Beach TickerState] load beach ticker state success"
const LOAD_BEACH_TICKER_STATE_FAILURE = "[Beach TickerState] load beach ticker state failure"
const BEACH_MATCH_UPDATE = "[Beach TickerState] beach match update"

export interface LoadBeachTickerStatePayload {
  hierarchies: BeachTickerHierarchies
  associations: BeachTickerAssociations
  matchSeries: BeachTickerMatchSeriesByUuid 
  matchDays: BeachTickerMatchDays
  matchStates: BeachTickerMatchStatesByUuid
  settings: BeachTickerSettings
}

interface LoadBeachTickerStateSuccessAction {
  readonly type: typeof LOAD_BEACH_TICKER_STATE_SUCCESS
  payload: LoadBeachTickerStatePayload
}

interface LoadBeachTickerStateFailureAction {
  readonly type: typeof LOAD_BEACH_TICKER_STATE_FAILURE
  payload: string
}

interface BeachMatchUpdateAction {
  readonly type: typeof BEACH_MATCH_UPDATE
  payload: BeachTickerMatchState
}

export const loadBeachTickerStateSuccess = (payload: LoadBeachTickerStatePayload): LoadBeachTickerStateSuccessAction => ({
  type: LOAD_BEACH_TICKER_STATE_SUCCESS,
  payload
})

export const loadTickerStateFailure = (payload: string): LoadBeachTickerStateFailureAction => ({
  type: LOAD_BEACH_TICKER_STATE_FAILURE,
  payload
})

export const matchUpdate = (payload: BeachTickerMatchState): BeachMatchUpdateAction => ({
  type: BEACH_MATCH_UPDATE,
  payload
})

export type BeachTickerStateAction =
  | LoadBeachTickerStateSuccessAction
  | LoadBeachTickerStateFailureAction
  | BeachMatchUpdateAction;

export const beachTickerStateReducer = (
  state: BeachTickerState = initialBeachTickerState,
  action: BeachTickerStateAction): BeachTickerState => {

  switch (action.type) {

    case LOAD_BEACH_TICKER_STATE_FAILURE: {
      return {
        ...state,
        loading: false,
        error: "Fehler beim laden der Daten, bitte versuchen Sie es erneut."
      }
    }

    case LOAD_BEACH_TICKER_STATE_SUCCESS: {
      const payload = action.payload

      return {
        ...state,
        matchSeries: payload.matchSeries,
        matchDays: payload.matchDays,
        settings: payload.settings,
        matchStatesByUuid: payload.matchStates,
        hierarchies: payload.hierarchies,
        associations: payload.associations,
        loading: false
      }
    }

    case BEACH_MATCH_UPDATE: {
      const matchState = action.payload
      return {
        ...state,
        matchStatesByUuid: {
          ...state.matchStatesByUuid,
          [matchState.matchUuid]: {
            ...state.matchStatesByUuid[matchState.matchUuid],
            ...matchState
          }
        }
      }
    }

    default: {
      return state
    }

  }

}


export const mapBeachTickerMatch = (match: BeachTickerCachedMatch, matchSeries: BeachTickerCachedMatchSeries): BeachTickerMatch => {
  const matchDate = new Date(match.date)
  const teams = matchSeries.teams
  const team1 = teams.find(t => t.id === match.team1) as BeachTickerApiTeam
  const team2 = teams.find(t => t.id === match.team2) as BeachTickerApiTeam
  return {
    ...match,
    team1,
    team2,
    formattedDate: formatDate(matchDate),
    formattedTime: formatTime(matchDate),
    matchSeries: matchSeries.id
  }
}

export const mapBeachTickerMatchDay = (matchDay: TickerApiMatchDay<BeachTickerCachedMatch>, matchSeries: ByUuid<BeachTickerCachedMatchSeries>): BeachTickerMatchDay => {
  const matches = matchDay.matches.map(m => {
    const ms = matchSeries[m.matchSeries]
    if (!ms) {
      return null
    }
    return mapBeachTickerMatch(m, ms)
  }).filter(x => !!x) as BeachTickerMatch[]
  return {
    matches,
    date: matchDay.date,
    formattedDate: getDayName(new Date(matchDay.date))
  }
}

export const mapBeachTickerMatchSeries = (associations: BeachTickerAssociations, matchSeries: ByUuid<BeachTickerCachedMatchSeries>): ByUuid<BeachTickerMatchSeries> => {
  return Object.values(matchSeries).reduce((result, ms) => {
    const association = associations.find(a => a.id === ms.association)
    return {
      ...result,
      [ms.id]: {
      id: ms.id,
      name: ms.name,
      shortName: ms.shortName,
      gender: ms.gender,
      association: association,
      hierarchy: association?.hierarchies.find(id => id === ms.hierarchy),
      matches: ms.matches,
      teams: ms.teams
      }
    } as ByUuid<BeachTickerMatchSeries>
  }, {} as ByUuid<BeachTickerMatchSeries>)

}
